import * as React from 'react'
import { AppView, AppMainView } from '../../../components/AppView'
import AppHeader from '../../../components/AppView/Header'
import { API, IInvoice, InvoiceStatus, PermissionNames } from '@getgreenline/homi-shared'
import { RouteComponentProps, browserHistory, InjectedRouter, PlainRoute, Link } from 'react-router'
import { CurrentCompanyStore } from '../../../stores/CurrentCompanyStore'
import { notification, Button, Alert, Row, Col, Tooltip } from 'antd'
import { parseErrorMsg } from '../../../utilities/helpers'
import { inject, observer } from 'mobx-react'
import { InvoiceDetailHeader } from './InvoiceDetail/Header/InvoiceDetailHeader'
import { Routes } from '../../../constants/Routes'
import { LoadingAnimation } from '../../../components/LoadingAnimation'
import { CreateInvoiceDetail } from './InvoiceDetail/InvoiceDetailStore'
import { CompleteInvoiceDetailButton } from './InvoiceDetail/Header/CompleteInvoiceDetailButton'
import { CancelInvoiceDetailButton } from './InvoiceDetail/Header/CancelInvoiceDetailButton'
import { InvoiceDescription } from './InvoiceDetail/Main/InvoiceDescription'
import { InvoiceDetailTable } from './InvoiceDetail/Main/InvoiceDetailTable'
import { InvoiceSummaryTable } from './InvoiceDetail/Main/InvoiceSummaryTable'
import Helmet from 'react-helmet'
import { TaxModels } from '@getgreenline/products'
import { ArrowRightOutlined } from '@ant-design/icons'
import { NoPermissionsResult } from '../../../components/NoPermissionsResult'
import { CurrentUserStore } from '../../../stores/CurrentUserStore'

interface Params {
  purchaseOrderId?: string
}

interface Props extends RouteComponentProps<Params, {}> {
  router: InjectedRouter
  route: PlainRoute
  currentCompanyStore: CurrentCompanyStore
  currentUserStore: CurrentUserStore
}

@inject('currentCompanyStore', 'currentUserStore')
@observer
export class InvoiceDetail extends React.Component<Props> {
  createInvoice = new CreateInvoiceDetail()

  company = this.props.currentCompanyStore.company!

  widthBreakPoint = 800

  notificationConfig = {
    duration: 0,
    top: 55,
  }

  componentDidMount = async () => {
    const { supplierStore, productStore } = this.props.currentCompanyStore

    if (supplierStore && !supplierStore.suppliers) {
      supplierStore!.getSuppliers()
    }

    if (productStore && !productStore.taxGroups) {
      productStore!.getTaxGroups()
    }

    this.props.router.setRouteLeaveHook(this.props.route, this.routerWillLeave)

    if (!this.invoiceId) {
      this.createInvoice.setIsEditMode(true)
      return
    }

    await this.loadInvoiceDetail(this.invoiceId)
  }

  routerWillLeave = () => {
    const { isEdited, isSaved } = this.createInvoice

    if (isEdited && !isSaved) {
      return 'Your work is not saved! Are you sure you want to leave?'
    }
  }

  createCustomError = (customErrorMessage: string) => {
    return notification.error({
      message: 'Error',
      description: customErrorMessage,
      ...this.notificationConfig,
    })
  }

  loadInvoiceDetail = async (invoiceId: string): Promise<void> => {
    try {
      const invoiceDetail = await API.getInvoiceById(this.company.id, invoiceId)

      this.createInvoice.setInvoiceDetail(invoiceDetail)
      this.createInvoice.populate(invoiceDetail)

      const matchingTaxGroup = this.getInvoiceTaxGroup(invoiceDetail)

      if (matchingTaxGroup) {
        this.createInvoice.setTaxGroup(matchingTaxGroup)

        /**
         * `setTaxGroup` sets `isEdited` to true. But here, customers didn't actually make any changes, so we should set `isEdited` back to false
         */
        this.createInvoice.setIsEdited(false)
      }
    } catch (error) {
      this.createCustomError(
        parseErrorMsg(error) || 'There was an error loading your purchase order',
      )
    }
  }

  getInvoiceTaxGroup = (invoiceDetail: IInvoice): TaxModels.TaxGroup | undefined => {
    const taxGroups = this.props.currentCompanyStore!.productStore!.taxGroups || []

    return taxGroups.find((taxGroup) => taxGroup.id === invoiceDetail.taxGroupId)
  }

  onBackClick = () => {
    const pathname = Routes.DASHBOARD_INVOICES(this.company.id)
    const query = browserHistory.getCurrentLocation().query

    browserHistory.push({ pathname, query })
  }

  get invoiceId(): string | undefined {
    return this.props.params.purchaseOrderId
  }

  get appTitle(): string {
    const { invoiceDetail } = this.createInvoice
    return invoiceDetail?.customId || 'Create PO'
  }

  get alertDescription(): string | undefined {
    const status = this.createInvoice.invoiceDetail?.status

    if (status === InvoiceStatus.PENDING) {
      return 'Inventory has not been checked in'
    } else if (status === InvoiceStatus.IN_PROGRESS) {
      return `Inventory has not been checked in. Set to be "completed" when you receive your product.`
    } else if (
      status === InvoiceStatus.COMPLETED &&
      this.createInvoice.isEditMode &&
      !this.createInvoice.isCompletedInCurrentMonth
    ) {
      return 'To help maintain compliance, you cannot edit PO quantities from prior months. Please perform an audit to update quantities.'
    } else if (status === InvoiceStatus.COMPLETED) {
      return 'Inventory has been checked in. Quantities of current month and costs for products that are not lot-tracked can still be edited.'
    } else {
      return 'This purchase order can no longer be edited.'
    }
  }

  get alertType(): 'success' | 'warning' | 'error' {
    const status = this.createInvoice.invoiceDetail?.status
    let alertType: 'success' | 'warning' | 'error' = 'warning'
    if (
      status === InvoiceStatus.COMPLETED &&
      this.createInvoice.isEditMode &&
      !this.createInvoice.isCompletedInCurrentMonth
    ) {
      alertType = 'warning'
    } else if (status === InvoiceStatus.COMPLETED) {
      alertType = 'success'
    } else if (status === InvoiceStatus.CANCELLED) {
      alertType = 'error'
    }
    return alertType
  }

  get isEditViewButtonVisible(): boolean {
    const { invoiceDetail } = this.createInvoice

    return !invoiceDetail || (invoiceDetail && invoiceDetail.status !== InvoiceStatus.CANCELLED)
  }

  render() {
    const userCanViewPurchaseOrders = this.props.currentUserStore.hasPermissionAtEntity(
      PermissionNames.CAN_VIEW_PURCHASE_ORDERS,
      this.company.id,
    )

    if (!userCanViewPurchaseOrders) {
      return <NoPermissionsResult permissionName={PermissionNames.CAN_VIEW_PURCHASE_ORDERS} />
    }

    const { isEditMode, isEditable, invoiceDetail, isNewInvoiceDetail, renderedStatus } =
      this.createInvoice

    return (
      <AppView column>
        <Helmet>
          <title>Purchase orders | Purchase order detail</title>
          <meta name='page-loaded' content='true' />
        </Helmet>

        {this.invoiceId && !invoiceDetail ? (
          <LoadingAnimation />
        ) : (
          <React.Fragment>
            <AppHeader title={this.appTitle} backVisible onBackClick={this.onBackClick}>
              <InvoiceDetailHeader
                createInvoice={this.createInvoice}
                getInvoiceTaxGroup={this.getInvoiceTaxGroup}
                widthBreakPoint={this.widthBreakPoint}
              />
            </AppHeader>

            <AppMainView className='background-grey'>
              <Row className='d-flex justify-content-center'>
                <Col xxl={19} xl={24} lg={24} md={24} sm={24} xs={24}>
                  <Alert
                    className='mb-2'
                    style={{ marginTop: -8 }}
                    message={
                      <Link
                        to={Routes.DASHBOARD_SAMPLE_DIRECT(this.company?.id)}
                        style={{ color: 'black', textDecorationLine: 'underline' }}
                      >
                        Check out the latest samples
                        <ArrowRightOutlined style={{ marginLeft: '6px' }} />
                      </Link>
                    }
                    type='success'
                  />
                  <div>
                    <div className='d-flex justify-content-between flex-wrap'>
                      {this.isEditViewButtonVisible &&
                        (this.createInvoice.isEdited ? (
                          <Tooltip title='Please save first'>
                            <Button
                              className='mr-2 mb-3'
                              icon={isEditMode ? 'eye' : 'edit'}
                              disabled
                            >
                              {`${isEditMode ? 'View' : 'Edit'} purchase order`}
                            </Button>
                          </Tooltip>
                        ) : (
                          <Button
                            className='mr-2 mb-3'
                            icon={isEditMode ? 'eye' : 'edit'}
                            onClick={() => this.createInvoice.setIsEditMode(!isEditMode)}
                          >
                            {`${isEditMode ? 'View' : 'Edit'} purchase order`}
                          </Button>
                        ))}

                      {invoiceDetail && !isEditMode && isEditable && (
                        <div className='d-flex mb-3'>
                          <CancelInvoiceDetailButton
                            className='mr-1'
                            title='Cancel'
                            createInvoice={this.createInvoice}
                          />

                          <CompleteInvoiceDetailButton
                            className='ml-1'
                            title='Complete'
                            createInvoice={this.createInvoice}
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  {!isNewInvoiceDetail && (
                    <Alert
                      showIcon
                      className='mb-3'
                      type={this.alertType}
                      message={renderedStatus?.name}
                      description={this.alertDescription}
                    />
                  )}

                  <InvoiceDescription createInvoice={this.createInvoice} />

                  <InvoiceDetailTable createInvoice={this.createInvoice} />

                  <Row className='d-flex justify-content-end'>
                    <Col xs={24} sm={24} md={16} lg={12} xl={12}>
                      <InvoiceSummaryTable createInvoice={this.createInvoice} />
                    </Col>
                  </Row>
                </Col>
              </Row>
            </AppMainView>
          </React.Fragment>
        )}
      </AppView>
    )
  }
}
